/** Création société : identité exigée pour pouvoir émettre une facture. */
import { useState } from "react";
import { Button } from "../components/Button";
import { CheckField } from "../components/CheckField";
import { Field } from "../components/Field";
import { legalFormIsSociete } from "../legal/mentions";
import { companyCreateGaps, emptyCompanyCreate, type CompanyCreateInput } from "./createInput";
import { useCompany } from "./CompanyProvider";
import "../components/confirmModal.css";

type CompanyCreateFormProps = {
  onDone?: () => void;
  onCancel?: () => void;
};

export function CompanyCreateForm({ onDone, onCancel }: CompanyCreateFormProps) {
  const { createCompany } = useCompany();
  const [draft, setDraft] = useState<CompanyCreateInput>(emptyCompanyCreate);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState("");

  const franchise = draft.vat_regime === "franchise";
  const societe = legalFormIsSociete(draft.legal_form);
  const gaps = companyCreateGaps(draft);

  function set<K extends keyof CompanyCreateInput>(key: K, value: CompanyCreateInput[K]) {
    setDraft((cur) => ({ ...cur, [key]: value }));
  }

  async function save() {
    setTried(true);
    setErr("");
    if (gaps.length > 0) {
      setErr(`Fiche incomplète : ${gaps.join(" · ")}.`);
      return;
    }
    setBusy(true);
    try {
      await createCompany({
        ...draft,
        legal_name: draft.legal_name.trim(),
        legal_form: draft.legal_form.trim(),
        siret: draft.siret.replace(/\D/g, ""),
        vat_number: franchise ? "" : draft.vat_number.trim(),
        share_capital: societe ? draft.share_capital.trim() : "",
        rcs_city: societe ? draft.rcs_city.trim() : "",
      });
      onDone?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Création impossible");
    } finally {
      setBusy(false);
    }
  }

  const miss = tried;

  return (
    <div className="fx-create">
      <p className="fx-create__hint">Ces champs sont exigés pour émettre une facture. Pour une micro, forme juridique EI.</p>
      <div className="fx-create-grid">
        <div className="is-span">
          <Field
            label="Nom de l’entreprise"
            required
            showMiss={miss && !draft.legal_name.trim()}
            value={draft.legal_name}
            onChange={(e) => set("legal_name", e.target.value)}
          />
        </div>
        <Field
          label="Forme juridique"
          required
          showMiss={miss && !draft.legal_form.trim()}
          value={draft.legal_form}
          placeholder="EI, SAS, SARL…"
          hint="EI pour une micro. Capital et RCS si société."
          onChange={(e) => set("legal_form", e.target.value)}
        />
        <Field
          label="SIRET"
          required
          inputMode="numeric"
          showMiss={miss && draft.siret.replace(/\D/g, "").length !== 14}
          value={draft.siret}
          onChange={(e) => set("siret", e.target.value.replace(/\D/g, "").slice(0, 14))}
        />
        <div className="is-span">
          <Field
            label="Rue"
            required
            showMiss={miss && !draft.address_line.trim()}
            value={draft.address_line}
            onChange={(e) => set("address_line", e.target.value)}
          />
        </div>
        <Field
          label="Code postal"
          required
          showMiss={miss && !draft.postal_code.trim()}
          value={draft.postal_code}
          onChange={(e) => set("postal_code", e.target.value)}
        />
        <Field
          label="Ville"
          required
          showMiss={miss && !draft.city.trim()}
          value={draft.city}
          onChange={(e) => set("city", e.target.value)}
        />
        <div className="is-span">
          <CheckField
            label="Micro-entreprise (franchise en base, sans n° de TVA)"
            checked={franchise}
            onChange={(next) => set("vat_regime", next ? "franchise" : "standard")}
          />
        </div>
        <div className="is-span">
          <Field
            label="Numéro de TVA"
            required={!franchise}
            disabled={franchise}
            showMiss={miss && !franchise && !draft.vat_number.trim()}
            value={franchise ? "" : draft.vat_number}
            hint={franchise ? "Grisé — régime micro" : undefined}
            onChange={(e) => set("vat_number", e.target.value)}
          />
        </div>
        <Field
          label="Capital social"
          required={societe}
          disabled={!societe}
          showMiss={miss && societe && !draft.share_capital.trim()}
          value={societe ? draft.share_capital : ""}
          hint={societe ? "Ex. 10 000 €" : "Grisé — EI / micro"}
          onChange={(e) => set("share_capital", e.target.value)}
        />
        <Field
          label="RCS / RM"
          required={societe}
          disabled={!societe}
          showMiss={miss && societe && !draft.rcs_city.trim()}
          value={societe ? draft.rcs_city : ""}
          hint={societe ? "Ville du greffe" : "Grisé — EI / micro"}
          onChange={(e) => set("rcs_city", e.target.value)}
        />
      </div>
      {err ? <p className="fx-create__err">{err}</p> : null}
      <div className="fx-modal__actions">
        {onCancel ? (
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Annuler
          </Button>
        ) : null}
        <Button disabled={busy} onClick={() => void save()}>
          {busy ? "Création…" : "Créer"}
        </Button>
      </div>
    </div>
  );
}
